import { keyframes } from "@emotion/react";
import { PinOutlined } from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VerticalAlignBottomIcon from "@mui/icons-material/VerticalAlignBottom";
import VerticalAlignTopIcon from "@mui/icons-material/VerticalAlignTop";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import FilterDropdown from "@/components/collections/FilterDropdown";
import { useCampaignBuilder } from "@/context/CampaignBuilderContext";

type Props = {
  countryLabel: string;
  selectedCount: number;
  canAddMore: boolean;
  onAddMore: () => void;
  allSelected: boolean;
  onSelectAll: () => void;
  onDelete: () => void;
  onMoveTop: () => void;
  onMoveBottom: () => void;
  onMoveToPosition: (position: number) => void;
  onReset: () => void;
  onExit: () => void;
  onSave: () => void;
  hasUnsavedChanges: boolean;
  categories: string[];
  activeCategories: string[];
  onToggleCategory: (cat: string) => void;
  onClearCategories: () => void;
};

const slideDown = keyframes`
  from { transform: translateY(-10px); opacity: 0; }
  to   { transform: translateY(0);    opacity: 1; }
`;

const btnBase =
  "h-8.5 rounded-btn font-lato font-bold text-[11px] tracking-[2px] uppercase px-4 transition-colors";

export default function EditToolbar({
  countryLabel,
  selectedCount,
  canAddMore,
  onAddMore,
  allSelected,
  onSelectAll,
  onDelete,
  onMoveTop,
  onMoveBottom,
  onMoveToPosition,
  onReset,
  onExit,
  onSave,
  hasUnsavedChanges,
  categories,
  activeCategories,
  onToggleCategory,
  onClearCategories,
}: Props) {
  const { openModal } = useCampaignBuilder();
  const [actionsAnchorEl, setActionsAnchorEl] = useState<HTMLElement | null>(null);
  const [moveToPos, setMoveToPos] = useState("");
  const hasSelection = selectedCount > 0;

  return (
    <Box
      sx={{
        px: 3,
        pt: 0,
        pb: 1.5,
        bgcolor: "white",
        animation: `${slideDown} 0.35s ease forwards`,
      }}
    >
      <div className="border-t-4 border-dark-dust mb-3" />

      {/* Row 1: Add More | Editing label + selected count | Reset / Exit / Save */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        {/* Left */}
        <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-start" }}>
          {
            <button
              type="button"
              onClick={onAddMore}
              disabled={!canAddMore}
              className={`${btnBase} border flex items-center gap-2 ${
                canAddMore
                  ? "border-rodeo-black text-rodeo-black bg-white hover:bg-light-dust"
                  : "border-smoke text-graphite bg-white opacity-40 cursor-not-allowed"
              }`}
            >
              <AddIcon
                sx={{
                  fontSize: 14,
                  color: canAddMore ? "var(--color-rodeo-black)" : "var(--color-graphite)",
                }}
              />
              Add More Gifts
            </button>
          }
        </Box>

        {/* Center */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexShrink: 0 }}>
          <Typography
            sx={{
              fontFamily: "var(--font-lato)",
              fontWeight: 600,
              fontSize: "18px",
              color: "var(--color-rodeo-black)",
              whiteSpace: "nowrap",
            }}
          >
            {(() => {
              const spaceIdx = countryLabel.indexOf(" ");
              const flag = spaceIdx !== -1 ? countryLabel.slice(0, spaceIdx) : "";
              const name = spaceIdx !== -1 ? countryLabel.slice(spaceIdx + 1) : countryLabel;
              return (
                <>
                  {flag} Editing {name} Collection
                </>
              );
            })()}
          </Typography>
          <Typography
            sx={{
              fontFamily: "var(--font-lato)",
              fontSize: "16px",
              color: "var(--color-graphite)",
              whiteSpace: "nowrap",
            }}
          >
            {selectedCount} selected
          </Typography>
        </Box>

        {/* Right */}
        <Box sx={{ flex: 1, display: "flex", gap: 1, justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={onReset}
            className={`${btnBase} border border-rodeo-black text-rodeo-black hover:bg-clear-dust`}
          >
            Reset
          </button>
          <button
            type="button"
            onClick={() => {
              if (hasUnsavedChanges) {
                openModal({
                  context: "unsavedChanges",
                  onPrimary: onSave,
                  onSecondary: onExit,
                });
              } else {
                onExit();
              }
            }}
            className={`${btnBase} border border-rodeo-black text-rodeo-black hover:bg-clear-dust`}
          >
            Exit
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={!hasUnsavedChanges}
            className={`${btnBase} ${
              hasUnsavedChanges
                ? "bg-dark-dust text-white hover:opacity-90"
                : "bg-smoke text-graphite cursor-not-allowed"
            }`}
          >
            Save
          </button>
        </Box>
      </Box>

      {/* Row 2: Checkbox + N selected | Action on Selected | Viewing all categories */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 3,
          bgcolor: "var(--color-clear-dust)",
          border: "1px solid var(--color-smoke)",
          borderRadius: "8px",
          p: "12px",
          mt: 2,
        }}
      >
        {/* Checkbox */}
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <span
            className="relative flex items-center justify-center shrink-0 rounded-[3px] transition-colors"
            style={{
              width: 18,
              height: 18,
              border: `2px solid var(--color-dark-dust)`,
              backgroundColor:
                allSelected || (selectedCount > 0 && !allSelected)
                  ? "var(--color-dark-dust)"
                  : "var(--color-white)",
            }}
          >
            <input
              type="checkbox"
              checked={allSelected}
              onChange={onSelectAll}
              className="absolute inset-0 opacity-0 w-full h-full cursor-pointer m-0"
            />
            {allSelected && (
              <svg width="10" height="8" viewBox="0 0 10 8" fill="none" aria-hidden="true">
                <path
                  d="M1 4l3 3 5-6"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
            {!allSelected && selectedCount > 0 && (
              <svg width="10" height="2" viewBox="0 0 10 2" fill="none" aria-hidden="true">
                <path d="M1 1h8" stroke="white" strokeWidth="2" strokeLinecap="round" />
              </svg>
            )}
          </span>
          <Typography
            sx={{
              fontFamily: "var(--font-lato)",
              fontSize: "13px",
              color: "var(--color-rodeo-black)",
            }}
          >
            Select all
          </Typography>
        </label>

        {/* Action on Selected dropdown */}
        <Box>
          <button
            type="button"
            onClick={(e) => hasSelection && setActionsAnchorEl(e.currentTarget)}
            disabled={!hasSelection}
            className={`${btnBase} border flex items-center gap-1 ${
              hasSelection
                ? "border-dark-dust text-rodeo-black hover:bg-clear-dust"
                : "border-smoke text-graphite opacity-40 cursor-not-allowed"
            }`}
          >
            <MoreVertIcon sx={{ fontSize: 18 }} />
            Action on Selected
            <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
          </button>
          <Menu
            anchorEl={actionsAnchorEl}
            open={Boolean(actionsAnchorEl)}
            onClose={() => setActionsAnchorEl(null)}
            slotProps={{
              paper: {
                sx: {
                  fontFamily: "var(--font-lato)",
                  border: "1px solid var(--color-smoke)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  borderRadius: "var(--radius-btn, 6px)",
                  minWidth: 200,
                },
              },
            }}
          >
            <MenuItem
              onClick={() => {
                onDelete();
                setActionsAnchorEl(null);
              }}
              sx={{
                fontFamily: "var(--font-lato)",
                fontSize: "14px",
                color: "var(--color-good-wine)",
                gap: 1,
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
              Delete
            </MenuItem>
            <MenuItem
              onClick={() => {
                onMoveTop();
                setActionsAnchorEl(null);
              }}
              sx={{ fontFamily: "var(--font-lato)", fontSize: "14px", gap: 1 }}
            >
              <VerticalAlignTopIcon sx={{ fontSize: 16, color: "var(--color-graphite)" }} />
              Move to Top
            </MenuItem>
            <MenuItem
              onClick={() => {
                onMoveBottom();
                setActionsAnchorEl(null);
              }}
              sx={{ fontFamily: "var(--font-lato)", fontSize: "14px", gap: 1 }}
            >
              <VerticalAlignBottomIcon sx={{ fontSize: 16, color: "var(--color-graphite)" }} />
              Move to Bottom
            </MenuItem>
            <Divider />
            <MenuItem
              disableRipple
              disableTouchRipple
              sx={{
                fontFamily: "var(--font-lato)",
                fontSize: "14px",
                gap: 1,
                cursor: "default",
                "&:hover": { bgcolor: "transparent" },
              }}
            >
              <PinOutlined sx={{ fontSize: 16, color: "var(--color-graphite)", flexShrink: 0 }} />
              <span style={{ flexShrink: 0 }}>Move to</span>
              <input
                type="number"
                min={1}
                value={moveToPos}
                onChange={(e) => setMoveToPos(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && moveToPos) {
                    onMoveToPosition(Number(moveToPos));
                    setMoveToPos("");
                    setActionsAnchorEl(null);
                  }
                }}
                placeholder="#"
                style={{
                  width: 48,
                  padding: "2px 6px",
                  fontSize: 13,
                  border: "1px solid var(--color-smoke)",
                  borderRadius: 4,
                  outline: "none",
                  fontFamily: "var(--font-lato)",
                  MozAppearance: "textfield",
                }}
              />
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!moveToPos) return;
                  onMoveToPosition(Number(moveToPos));
                  setMoveToPos("");
                  setActionsAnchorEl(null);
                }}
                style={{
                  padding: "2px 10px",
                  fontSize: 11,
                  fontWeight: 700,
                  fontFamily: "var(--font-lato)",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  background: "var(--color-dark-dust)",
                  color: "white",
                  border: "none",
                  borderRadius: 4,
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              >
                Go
              </button>
            </MenuItem>
          </Menu>
        </Box>

        {/* Viewing all categories — right-aligned */}
        <Box sx={{ ml: "auto" }}>
          <FilterDropdown
            label="Viewing all categories"
            options={categories}
            selected={activeCategories}
            onToggle={onToggleCategory}
            onClear={onClearCategories}
          />
        </Box>
      </Box>
    </Box>
  );
}
